import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { Client } from '../../../services/clientService';
import { currencyService } from '../../../services/currencyService';
import { getInvoicePdfUrl } from '../../../services/adminPaymentLinkService';
import type { Service } from '../../../services/servicesStore';
import type {
  AdminSubscription,
  SubscriptionActivityEntry,
  SubscriptionBillingStatus,
  SubscriptionInterval,
  SubscriptionPaymentMode,
  SubscriptionServiceStatus,
  SubscriptionStatus,
} from '../../../services/adminSubscriptionService';
import type { TeamSubscriptionRecord } from '../../../services/adminTeamWorkspaceService';

interface SubscriptionDetailsModalProps {
  subscription: AdminSubscription | null;
  clients: Client[];
  services: Service[];
  defaultMode?: 'view' | 'create';
  onClose: () => void;
  onSave: (subscription: AdminSubscription) => void;
  onMarkPaid?: (subscription: AdminSubscription) => void;
  onDeferBilling?: (subscription: AdminSubscription, days: number) => void;
  onAddInternalNote?: (subscription: AdminSubscription) => void;
  teamRecord?: TeamSubscriptionRecord | null;
  onSaveTeamRecord?: (
    subscriptionId: string,
    patch: Pick<TeamSubscriptionRecord, 'ownerName' | 'ownerEmail'>
  ) => void;
  actionInFlight?: string;
}

const INTERVAL_OPTIONS: Array<{ value: SubscriptionInterval; label: string }> = [
  { value: 'monthly', label: 'Mensuel' },
  { value: 'quarterly', label: 'Trimestriel' },
  { value: 'yearly', label: 'Annuel' },
];

const PAYMENT_MODE_OPTIONS: Array<{ value: SubscriptionPaymentMode; label: string }> = [
  { value: 'recurring', label: 'Récurrent' },
  { value: 'one_time', label: 'Unique' },
];

const STATUS_OPTIONS: Array<{ value: SubscriptionStatus; label: string }> = [
  { value: 'trial', label: 'Essai' },
  { value: 'active', label: 'Actif' },
  { value: 'paused', label: 'En pause' },
  { value: 'past_due', label: 'En retard' },
  { value: 'canceled', label: 'Annule' },
  { value: 'expired', label: 'Expire' },
];

const BILLING_STATUS_OPTIONS: Array<{
  value: SubscriptionBillingStatus;
  label: string;
}> = [
  { value: 'scheduled', label: 'Facturation planifiee' },
  { value: 'paid', label: 'Prelevement paye' },
  { value: 'overdue', label: 'Paiement en retard' },
  { value: 'unpaid', label: 'Impayee' },
];

const SERVICE_STATUS_OPTIONS: Array<{
  value: SubscriptionServiceStatus;
  label: string;
}> = [
  { value: 'pending_setup', label: 'Setup en attente' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'active_delivery', label: 'Livraison active' },
  { value: 'delivered', label: 'Livre / actif' },
  { value: 'paused', label: 'En pause' },
  { value: 'closed', label: 'Cloture' },
];

const STATUS_BADGES: Record<SubscriptionStatus, string> = {
  trial: 'bg-slate-100 text-slate-700',
  active: 'bg-emerald-100 text-emerald-700',
  paused: 'bg-amber-100 text-amber-700',
  past_due: 'bg-red-100 text-red-700',
  canceled: 'bg-gray-100 text-gray-600',
  expired: 'bg-orange-100 text-orange-700',
};

const BILLING_BADGES: Record<SubscriptionBillingStatus, string> = {
  scheduled: 'bg-indigo-100 text-indigo-700',
  paid: 'bg-emerald-100 text-emerald-700',
  overdue: 'bg-red-100 text-red-700',
  unpaid: 'bg-amber-100 text-amber-700',
};

const SERVICE_BADGES: Record<SubscriptionServiceStatus, string> = {
  pending_setup: 'bg-slate-100 text-slate-700',
  onboarding: 'bg-blue-100 text-blue-700',
  active_delivery: 'bg-indigo-100 text-indigo-700',
  delivered: 'bg-emerald-100 text-emerald-700',
  paused: 'bg-amber-100 text-amber-700',
  closed: 'bg-gray-100 text-gray-600',
};

const formatDateInput = (value?: string) => {
  if (!value) {
    return '';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  return parsed.toISOString().slice(0, 10);
};

const formatDateTime = (value?: string) => {
  if (!value) {
    return 'Non renseigne';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Non renseigne';
  }

  return parsed.toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const SubscriptionDetailsModal = ({
  subscription,
  clients,
  services,
  defaultMode = 'view',
  onClose,
  onSave,
  onMarkPaid,
  onDeferBilling,
  onAddInternalNote,
  teamRecord = null,
  onSaveTeamRecord,
  actionInFlight = '',
}: SubscriptionDetailsModalProps) => {
  const [editMode, setEditMode] = useState(defaultMode === 'create');
  const [formData, setFormData] = useState<AdminSubscription | null>(subscription);
  const [displayCountry, setDisplayCountry] = useState(currencyService.getCountry());
  const [ownerNameDraft, setOwnerNameDraft] = useState('');
  const [ownerEmailDraft, setOwnerEmailDraft] = useState('');
  const submitRequestedRef = useRef(false);

  useEffect(() => {
    setFormData(subscription);
    setEditMode(defaultMode === 'create');
    submitRequestedRef.current = false;
  }, [subscription, defaultMode]);

  useEffect(() => {
    setOwnerNameDraft(teamRecord?.ownerName || '');
    setOwnerEmailDraft(teamRecord?.ownerEmail || '');
  }, [teamRecord, subscription?.id]);

  useEffect(() => {
    return currencyService.subscribe((nextCountry) => {
      setDisplayCountry(nextCountry);
    });
  }, []);

  useEffect(() => {
    if (!subscription) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [subscription, onClose]);

  if (!subscription || !formData) {
    return null;
  }

  const setField = <K extends keyof AdminSubscription>(
    field: K,
    value: AdminSubscription[K]
  ) => {
    setFormData((current) => (current ? { ...current, [field]: value } : current));
  };

  const displayAmountLabel = currencyService.formatAmountForDisplay(
    formData.amount,
    formData.currencyCode,
    {
      country: displayCountry,
      display: 'code',
    }
  );
  const originalAmountLabel = currencyService.formatCurrencyAmount(
    formData.amount,
    formData.currencyCode,
    { display: 'code' }
  );

  const handleClientChange = (clientId: string) => {
    const client = clients.find((entry) => entry.id === clientId);
    if (!client || !formData) {
      return;
    }

    setFormData({
      ...formData,
      clientId: client.id,
      clientName: client.name,
      clientEmail: client.email,
      clientPhone: client.phone || '',
      clientCompany: client.company || '',
      serviceId: formData.serviceId || client.serviceId || '',
      serviceName: formData.serviceName || client.serviceName || '',
      amount: formData.amount || client.totalAmount || 0,
      currencyCode: formData.currencyCode || client.currencyCode || 'EUR',
    });
  };

  const handleServiceChange = (serviceId: string) => {
    const service = services.find((entry) => entry.id === serviceId);
    if (!service || !formData) {
      return;
    }

    setFormData({
      ...formData,
      serviceId: service.id,
      serviceName: service.name,
      amount:
        formData.amount > 0
          ? formData.amount
          : currencyService.convertAmount(
              service.price,
              'EUR',
              formData.currencyCode || 'EUR'
            ),
    });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!submitRequestedRef.current) {
      return;
    }
    submitRequestedRef.current = false;

    onSave(formData);
    setEditMode(false);
  };

  const armSubmitIntent = () => {
    submitRequestedRef.current = true;
    window.setTimeout(() => {
      submitRequestedRef.current = false;
    }, 0);
  };

  const activityLog = [...(formData.activityLog || [])].sort(
    (left: SubscriptionActivityEntry, right: SubscriptionActivityEntry) =>
      new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
  );

  return (
    <AnimatePresence>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.96 }}
          className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-6 py-4">
            <div>
              <h2 className="text-xl font-semibold">
                {editMode ? "Modifier l'abonnement" : "Fiche abonnement"}
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                {formData.subscriptionNumber}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                submitRequestedRef.current = false;
                onClose();
              }}
              aria-label="Fermer la fiche abonnement"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-2xl leading-none text-gray-700 transition-colors hover:bg-gray-200"
            >
              ×
            </button>
          </div>

          <div className="overflow-y-auto p-6">
            {editMode ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Numero abonnement
                    </label>
                    <input
                      type="text"
                      value={formData.subscriptionNumber}
                      onChange={(event) =>
                        setField('subscriptionNumber', event.target.value)
                      }
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Frequence
                    </label>
                    <select
                      value={formData.interval}
                      onChange={(event) =>
                        setField('interval', event.target.value as SubscriptionInterval)
                      }
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    >
                      {INTERVAL_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Statut abonnement
                    </label>
                    <select
                      value={formData.status}
                      onChange={(event) =>
                        setField('status', event.target.value as SubscriptionStatus)
                      }
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    >
                      {STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Statut service
                    </label>
                    <select
                      value={formData.serviceStatus}
                      onChange={(event) =>
                        setField(
                          'serviceStatus',
                          event.target.value as SubscriptionServiceStatus
                        )
                      }
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    >
                      {SERVICE_STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Type paiement
                    </label>
                    <select
                      value={formData.paymentMode}
                      onChange={(event) =>
                        setField('paymentMode', event.target.value as SubscriptionPaymentMode)
                      }
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    >
                      {PAYMENT_MODE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                  <div className="mb-4 text-sm font-semibold text-gray-700">Assignation interne</div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Proprietaire du compte
                      </label>
                      <input
                        type="text"
                        value={ownerNameDraft}
                        onChange={(event) => setOwnerNameDraft(event.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                        placeholder="Nom du responsable"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Email responsable
                      </label>
                      <input
                        type="email"
                        value={ownerEmailDraft}
                        onChange={(event) => setOwnerEmailDraft(event.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                        placeholder="responsable@agence.com"
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() =>
                        onSaveTeamRecord?.(formData.id, {
                          ownerName: ownerNameDraft,
                          ownerEmail: ownerEmailDraft,
                        })
                      }
                      className="rounded-lg bg-sky-50 px-4 py-2 text-sm font-medium text-sky-700 transition-colors hover:bg-sky-100"
                    >
                      Enregistrer responsable
                    </button>
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                  <div className="mb-4 text-sm font-semibold text-gray-700">Client</div>
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Fiche client
                      </label>
                      <select
                        value={formData.clientId}
                        onChange={(event) => handleClientChange(event.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                      >
                        <option value="">Selectionner un client</option>
                        {clients.map((client) => (
                          <option key={client.id} value={client.id}>
                            {client.name} - {client.email}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Nom
                      </label>
                      <input
                        type="text"
                        value={formData.clientName}
                        onChange={(event) => setField('clientName', event.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                        required
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Email
                      </label>
                      <input
                        type="email"
                        value={formData.clientEmail}
                        onChange={(event) => setField('clientEmail', event.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                        required
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Telephone
                      </label>
                      <input
                        type="text"
                        value={formData.clientPhone}
                        onChange={(event) => setField('clientPhone', event.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Entreprise
                      </label>
                      <input
                        type="text"
                        value={formData.clientCompany}
                        onChange={(event) => setField('clientCompany', event.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                      />
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                  <div className="mb-4 text-sm font-semibold text-gray-700">
                    Service & facturation
                  </div>
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Service
                      </label>
                      <select
                        value={formData.serviceId}
                        onChange={(event) => handleServiceChange(event.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                      >
                        <option value="">Selectionner un service</option>
                        {services.map((service) => (
                          <option key={service.id} value={service.id}>
                            {service.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Libelle service
                      </label>
                      <input
                        type="text"
                        value={formData.serviceName}
                        onChange={(event) => setField('serviceName', event.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                      />
                    </div>
                    <div className="grid grid-cols-[minmax(0,1fr)_120px] gap-3">
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          Montant
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.amount}
                          onChange={(event) =>
                            setField('amount', Number(event.target.value || 0))
                          }
                          className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          Devise
                        </label>
                        <select
                          value={formData.currencyCode}
                          onChange={(event) =>
                            setField('currencyCode', event.target.value.toUpperCase())
                          }
                          className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                        >
                          {currencyService.getSupportedCurrencyCodes().map((currencyCode) => (
                            <option key={currencyCode} value={currencyCode}>
                              {currencyCode}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Statut facturation
                      </label>
                      <select
                        value={formData.billingStatus}
                        onChange={(event) =>
                          setField(
                            'billingStatus',
                            event.target.value as SubscriptionBillingStatus
                          )
                        }
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                      >
                        {BILLING_STATUS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-gray-500">
                    Affichage admin actuel: {displayAmountLabel}. Montant d origine:
                    {' '}{originalAmountLabel}.
                  </p>
                </div>

                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Debut
                    </label>
                    <input
                      type="date"
                      value={formatDateInput(formData.startDate)}
                      onChange={(event) =>
                        setField(
                          'startDate',
                          event.target.value
                            ? new Date(`${event.target.value}T12:00:00`).toISOString()
                            : new Date().toISOString()
                        )
                      }
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Prochaine echeance
                    </label>
                    <input
                      type="date"
                      value={formatDateInput(formData.nextBillingDate)}
                      onChange={(event) =>
                        setField(
                          'nextBillingDate',
                          event.target.value
                            ? new Date(`${event.target.value}T12:00:00`).toISOString()
                            : ''
                        )
                      }
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Dernier paiement
                    </label>
                    <input
                      type="date"
                      value={formatDateInput(formData.lastBillingDate)}
                      onChange={(event) =>
                        setField(
                          'lastBillingDate',
                          event.target.value
                            ? new Date(`${event.target.value}T12:00:00`).toISOString()
                            : ''
                        )
                      }
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Fin / resiliation
                    </label>
                    <input
                      type="date"
                      value={formatDateInput(formData.endDate)}
                      onChange={(event) =>
                        setField(
                          'endDate',
                          event.target.value
                            ? new Date(`${event.target.value}T12:00:00`).toISOString()
                            : ''
                        )
                      }
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Commande liee
                    </label>
                    <input
                      type="text"
                      value={formData.linkedOrderNumber}
                      onChange={(event) =>
                        setField('linkedOrderNumber', event.target.value)
                      }
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Cle lien paiement
                    </label>
                    <input
                      type="text"
                      value={formData.paymentLinkKey}
                      onChange={(event) =>
                        setField('paymentLinkKey', event.target.value)
                      }
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    />
                  </div>
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Notes client
                    </label>
                    <textarea
                      rows={5}
                      value={formData.notes}
                      onChange={(event) => setField('notes', event.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Notes internes
                    </label>
                    <textarea
                      rows={5}
                      value={formData.internalNotes}
                      onChange={(event) =>
                        setField('internalNotes', event.target.value)
                      }
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 border-t pt-6">
                  {defaultMode !== 'create' && (
                    <button
                      type="button"
                      onClick={() => {
                        submitRequestedRef.current = false;
                        setEditMode(false);
                      }}
                      className="rounded-lg bg-gray-100 px-5 py-3 font-medium text-gray-700 transition-colors hover:bg-gray-200"
                    >
                      Annuler
                    </button>
                  )}
                  <button
                    type="submit"
                    onClick={armSubmitIntent}
                    className="rounded-lg bg-neon-orange px-5 py-3 font-medium text-white transition-colors hover:bg-[#FF4500]"
                  >
                    Enregistrer l&apos;abonnement
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                    <div className="text-sm text-gray-500">Statut abonnement</div>
                    <span
                      className={`mt-3 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                        STATUS_BADGES[formData.status]
                      }`}
                    >
                      {STATUS_OPTIONS.find((option) => option.value === formData.status)?.label}
                    </span>
                  </div>
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                    <div className="text-sm text-gray-500">Facturation</div>
                    <span
                      className={`mt-3 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                        BILLING_BADGES[formData.billingStatus]
                      }`}
                    >
                      {
                        BILLING_STATUS_OPTIONS.find(
                          (option) => option.value === formData.billingStatus
                        )?.label
                      }
                    </span>
                  </div>
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                    <div className="text-sm text-gray-500">Delivery / service</div>
                    <span
                      className={`mt-3 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                        SERVICE_BADGES[formData.serviceStatus]
                      }`}
                    >
                      {
                        SERVICE_STATUS_OPTIONS.find(
                          (option) => option.value === formData.serviceStatus
                        )?.label
                      }
                    </span>
                  </div>
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                    <div className="text-sm text-gray-500">Montant</div>
                    <div className="mt-3 text-2xl font-bold text-gray-900">
                      {displayAmountLabel}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      Base enregistree: {originalAmountLabel}
                    </div>
                  </div>
                </div>

                <div className="grid gap-6 lg:grid-cols-2">
                  <section className="rounded-2xl border border-gray-200 p-5">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Client & service
                    </h3>
                    <div className="mt-4 space-y-3 text-sm text-gray-700">
                      <div>
                        <strong>Client :</strong> {formData.clientName}
                      </div>
                      <div>
                        <strong>Email :</strong> {formData.clientEmail}
                      </div>
                      <div>
                        <strong>Entreprise :</strong>{' '}
                        {formData.clientCompany || 'Non renseignee'}
                      </div>
                      <div>
                        <strong>Telephone :</strong>{' '}
                        {formData.clientPhone || 'Non renseigne'}
                      </div>
                      <div>
                        <strong>Service :</strong> {formData.serviceName || 'Non renseigne'}
                      </div>
                      <div>
                        <strong>Frequence :</strong>{' '}
                        {
                          INTERVAL_OPTIONS.find(
                            (option) => option.value === formData.interval
                          )?.label
                        }
                      </div>
                      <div>
                        <strong>Type paiement :</strong>{' '}
                        {
                          PAYMENT_MODE_OPTIONS.find(
                            (option) => option.value === formData.paymentMode
                          )?.label
                        }
                      </div>
                    </div>
                  </section>

                  <section className="rounded-2xl border border-gray-200 p-5">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Calendrier & refs
                    </h3>
                    <div className="mt-4 space-y-3 text-sm text-gray-700">
                      <div>
                        <strong>Debut :</strong> {formatDateTime(formData.startDate)}
                      </div>
                      <div>
                        <strong>Prochaine echeance :</strong>{' '}
                        {formatDateTime(formData.nextBillingDate)}
                      </div>
                      <div>
                        <strong>Dernier paiement :</strong>{' '}
                        {formatDateTime(formData.lastBillingDate)}
                      </div>
                      <div>
                        <strong>Fin / resiliation :</strong>{' '}
                        {formatDateTime(formData.endDate)}
                      </div>
                      <div>
                        <strong>Commande liee :</strong>{' '}
                        {formData.linkedOrderNumber || 'Non reliee'}
                      </div>
                      <div>
                        <strong>Cle lien paiement :</strong>{' '}
                        {formData.paymentLinkKey ? (
                          <span className="inline-flex rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700">
                            {formData.paymentLinkKey}
                          </span>
                        ) : (
                          'Non reliee'
                        )}
                      </div>
                      <div>
                        <strong>Customer Mollie :</strong>{' '}
                        {formData.mollieCustomerId || 'Non renseigne'}
                      </div>
                      <div>
                        <strong>Subscription Mollie :</strong>{' '}
                        {formData.mollieSubscriptionId || 'Non renseigne'}
                      </div>
                      <div>
                        <strong>Statut récurrent :</strong>{' '}
                        {formData.recurringStatus || 'Non renseigne'}
                      </div>
                      <div>
                        <strong>Activation récurrente :</strong>{' '}
                        {formatDateTime(formData.recurringActivatedAt)}
                      </div>
                      {formData.paymentLinkKey && (
                        <div className="flex flex-wrap gap-2 pt-1">
                          <a
                            href={`/devis/${encodeURIComponent(formData.paymentLinkKey)}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
                          >
                            Ouvrir le devis client
                          </a>
                          <a
                            href={getInvoicePdfUrl(formData.paymentLinkKey)}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
                          >
                            Télécharger facture PDF
                          </a>
                        </div>
                      )}
                    </div>
                  </section>
                </div>

                <div className="grid gap-6 lg:grid-cols-2">
                  <section className="rounded-2xl border border-gray-200 p-5">
                    <h3 className="text-lg font-semibold text-gray-900">Notes client</h3>
                    <p className="mt-4 whitespace-pre-line text-sm leading-6 text-gray-700">
                      {formData.notes || 'Aucune note client pour le moment.'}
                    </p>
                  </section>
                  <section className="rounded-2xl border border-gray-200 p-5">
                    <h3 className="text-lg font-semibold text-gray-900">Notes internes</h3>
                    <p className="mt-4 whitespace-pre-line text-sm leading-6 text-gray-700">
                      {formData.internalNotes || 'Aucune note interne pour le moment.'}
                    </p>
                  </section>
                </div>

                <section className="rounded-2xl border border-gray-200 p-5">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Actions rapides
                      </h3>
                      <p className="mt-1 text-sm text-gray-600">
                        Gere rapidement la facturation et le suivi sans ouvrir le mode edition.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => onMarkPaid?.(formData)}
                        disabled={!onMarkPaid || actionInFlight !== ''}
                        className="rounded-lg bg-emerald-100 px-4 py-2 text-sm font-semibold text-emerald-800 transition-colors hover:bg-emerald-200 disabled:opacity-60"
                      >
                        {actionInFlight === 'mark_paid' ? 'Validation...' : 'Marquer paye'}
                      </button>
                      <button
                        type="button"
                        onClick={() => onDeferBilling?.(formData, 3)}
                        disabled={!onDeferBilling || actionInFlight !== ''}
                        className="rounded-lg bg-amber-100 px-4 py-2 text-sm font-semibold text-amber-800 transition-colors hover:bg-amber-200 disabled:opacity-60"
                      >
                        {actionInFlight === 'defer_3' ? 'Decalage...' : 'Decaler +3j'}
                      </button>
                      <button
                        type="button"
                        onClick={() => onDeferBilling?.(formData, 7)}
                        disabled={!onDeferBilling || actionInFlight !== ''}
                        className="rounded-lg bg-orange-100 px-4 py-2 text-sm font-semibold text-orange-800 transition-colors hover:bg-orange-200 disabled:opacity-60"
                      >
                        {actionInFlight === 'defer_7' ? 'Decalage...' : 'Decaler +7j'}
                      </button>
                      <button
                        type="button"
                        onClick={() => onAddInternalNote?.(formData)}
                        disabled={!onAddInternalNote || actionInFlight !== ''}
                        className="rounded-lg bg-sky-100 px-4 py-2 text-sm font-semibold text-sky-800 transition-colors hover:bg-sky-200 disabled:opacity-60"
                      >
                        {actionInFlight === 'add_note' ? 'Ajout...' : 'Ajouter note'}
                      </button>
                    </div>
                  </div>
                </section>

                <section className="rounded-2xl border border-gray-200 p-5">
                  <h3 className="text-lg font-semibold text-gray-900">Timeline activite</h3>
                  <div className="mt-4 space-y-4">
                    {activityLog.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-sm text-gray-500">
                        Aucune activite journalisee pour le moment.
                      </div>
                    ) : (
                      activityLog.map((entry) => (
                        <div key={entry.id} className="relative pl-6">
                          <div className="absolute left-0 top-2 h-2.5 w-2.5 rounded-full bg-neon-orange" />
                          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                              <div className="font-semibold text-gray-900">{entry.title}</div>
                              <div className="text-xs text-gray-500">
                                {formatDateTime(entry.createdAt)}
                              </div>
                            </div>
                            <div className="mt-2 text-sm leading-6 text-gray-700">
                              {entry.detail || 'Mise a jour sans detail supplementaire.'}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </section>

                <div className="flex justify-end gap-3 border-t pt-6">
                  <button
                    type="button"
                    onClick={() => {
                      submitRequestedRef.current = false;
                      setEditMode(true);
                    }}
                    className="rounded-lg bg-gray-100 px-5 py-3 font-medium text-gray-700 transition-colors hover:bg-gray-200"
                  >
                    Modifier
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      submitRequestedRef.current = false;
                      onClose();
                    }}
                    className="rounded-lg bg-neon-orange px-5 py-3 font-medium text-white transition-colors hover:bg-[#FF4500]"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default SubscriptionDetailsModal;
